import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Modal from 'react-bootstrap/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons'

const KeywordPicker = ({ label, selectedKeywords, defaultKeywords, onChange, disabled }) => {
  const [show, setShow] = useState(false)
  const [localKeywords, setLocalKeywords] = useState([])
  const [inputValue, setInputValue] = useState('')

  const handleOpen = () => {
    setLocalKeywords([...(selectedKeywords || [])])
    setInputValue('')
    setShow(true)
  }

  const handleClose = () => {
    setShow(false)
  }

  const handleSave = () => {
    onChange(localKeywords)
    setShow(false)
  }

  const addKeywords = (text) => {
    const newWords = text
      .split(/[\s,]+/)
      .map((w) => w.trim().toLowerCase())
      .filter((w) => w && !localKeywords.includes(w))
    if (newWords.length > 0) {
      setLocalKeywords((prev) => [...prev, ...newWords])
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addKeywords(inputValue)
      setInputValue('')
    }
  }

  const handleRemove = (keyword) => {
    setLocalKeywords((prev) => prev.filter((k) => k !== keyword))
  }

  const handleClearAll = () => {
    setLocalKeywords([])
  }

  const handleRevertToDefault = () => {
    setLocalKeywords([...(defaultKeywords || [])])
  }

  const count = (selectedKeywords || []).length

  return (
    <>
      <Button
        variant="light"
        size="sm"
        onClick={handleOpen}
        disabled={disabled}
      >
        {count} keyword{count !== 1 ? 's' : ''} configured
      </Button>

      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{label}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-3">
            <Form.Control
              type="text"
              size="sm"
              placeholder="Type keyword and press Enter (comma-separated for multiple)..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <Button
              variant="light"
              size="sm"
              onClick={handleRevertToDefault}
              className="text-nowrap"
            >
              Revert to Default
            </Button>
            <Button
              variant="light"
              size="sm"
              onClick={handleClearAll}
              className="text-nowrap"
            >
              Clear All
            </Button>
          </div>
          <div className="text-muted small mb-2">
            {localKeywords.length} keyword{localKeywords.length !== 1 ? 's' : ''}
          </div>
          <div className="d-flex flex-wrap gap-1">
            {localKeywords.map((keyword) => (
              <Badge
                key={keyword}
                bg="light"
                text="primary"
                className="d-inline-flex align-items-center border"
                style={{ cursor: 'pointer', fontSize: '0.85em' }}
                onClick={() => handleRemove(keyword)}
              >
                {keyword}
                <FontAwesomeIcon
                  icon={faTimesCircle}
                  className="ms-1 text-muted"
                  style={{ fontSize: '0.9em' }}
                />
              </Badge>
            ))}
            {localKeywords.length === 0 && (
              <span className="text-muted small">No keywords added</span>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Keywords
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

KeywordPicker.propTypes = {
  label: PropTypes.string.isRequired,
  selectedKeywords: PropTypes.arrayOf(PropTypes.string),
  defaultKeywords: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default KeywordPicker
